import axios from "axios";

// In preview / local-dev we point at the configured backend URL (the
// preview hostname). In any deployed environment (custom domain or default
// .emergent.host) we use SAME-ORIGIN so the browser never has to make a
// cross-site request — sidesteps Brave Shields / strict-cookie blocks /
// CORS edge cases entirely and is also a touch faster.
function pickApiBase() {
    if (typeof window === "undefined") return process.env.REACT_APP_BACKEND_URL;
    const host = window.location.hostname;
    const isPreviewOrLocal =
        host === "localhost" ||
        host === "127.0.0.1" ||
        /\.preview\.emergent(agent)?\.(com|host)$/i.test(host);
    return isPreviewOrLocal ? process.env.REACT_APP_BACKEND_URL : window.location.origin;
}

export const API = `${pickApiBase()}/api`;

export const api = axios.create({
    baseURL: API,
    withCredentials: true,
});

api.interceptors.request.use((config) => {
    const t = localStorage.getItem("hg_token");
    if (t) config.headers.Authorization = `Bearer ${t}`;
    return config;
});

// Hard subscription gate: any 402 from the API first attempts a silent
// `/payments/sync` reconciliation with Stripe (catches missed webhooks / closed
// post-checkout tabs). If the user is still gated afterwards, redirect to the
// paywall with the reason. If sync flipped them to premium, retry the original
// request transparently. Concurrent 402s share a single in-flight sync promise.
let _inflightSync = null;
let _lastSyncAt = 0;
const SYNC_COOLDOWN_MS = 60_000; // don't hammer Stripe within a minute

async function _maybeSync() {
    const now = Date.now();
    if (now - _lastSyncAt < SYNC_COOLDOWN_MS) return null;
    if (_inflightSync) return _inflightSync;
    _inflightSync = (async () => {
        try {
            const r = await axios.post(`${API}/payments/sync`, {}, {
                withCredentials: true,
                headers: { Authorization: localStorage.getItem("hg_token") ? `Bearer ${localStorage.getItem("hg_token")}` : undefined },
            });
            _lastSyncAt = Date.now();
            return r.data;
        } catch (_) {
            _lastSyncAt = Date.now();
            return null;
        } finally {
            _inflightSync = null;
        }
    })();
    return _inflightSync;
}

api.interceptors.response.use(
    (r) => r,
    async (err) => {
        const status = err?.response?.status;
        const cfg = err?.config || {};
        const isAuthed = !!localStorage.getItem("hg_token") || document.cookie.includes("session_token");
        const onPricing = typeof window !== "undefined" && window.location.pathname.startsWith("/pricing");

        if (status === 402 && isAuthed && !cfg.__autoSyncRetried && !onPricing) {
            cfg.__autoSyncRetried = true;
            const sync = await _maybeSync();
            if (sync && sync.premium) {
                // Stripe reconciled — silently retry the original request
                return api.request(cfg);
            }
        }

        if (status === 402) {
            const reason = err.response.data?.detail?.reason || "premium_required";
            if (!onPricing) {
                window.location.assign(`/pricing?paywall=1&reason=${encodeURIComponent(reason)}`);
            }
        }
        return Promise.reject(err);
    }
);
